import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { FiMail, FiEye, FiEyeOff, FiLock, FiArrowLeft, FiShield } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { LogoWhite } from '../../components/common/Logo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const userType = searchParams.get('type');
  
  const profileLabels = {
    cliente: { title: 'Cliente', subtitle: 'Acesse para comprar' },
    lojista: { title: 'Comerciante', subtitle: 'Acesse sua loja' },
    entregador: { title: 'Entregador', subtitle: 'Acesse suas entregas' }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const user = await login(email, password);
      
      switch (user.user_type) {
        case 'cliente':
          navigate('/cliente');
          break;
        case 'lojista':
          navigate('/lojista');
          break;
        case 'entregador':
          navigate('/entregador');
          break;
        case 'admin':
          navigate('/admin');
          break;
        default:
          navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A1A] via-[#2D2D2D] to-[#DC143C] flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
        >
          <FiArrowLeft />
          <span>Voltar</span>
        </button>

        {/* Profile Badge */}
        {userType && profileLabels[userType] && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 mb-6 text-center">
            <p className="text-white font-semibold">{profileLabels[userType].title}</p>
            <p className="text-white/70 text-sm">{profileLabels[userType].subtitle}</p>
          </div>
        )}

        {/* Logo and Brand */}
        <div className="text-center mb-6 md:mb-8">
          <div className="flex justify-center mb-4">
            <LogoWhite size="lg" showText={true} />
          </div>
          <p className="text-white/70 mt-2 text-sm md:text-base">Entregas rápido como um raio!</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-[#1A1A1A] mb-6">Entrar</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm animate-slideDown">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C]/20 focus:border-[#DC143C] outline-none transition-all text-base"
                  placeholder="seu@email.com"
                  required
                  autoComplete="email"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C]/20 focus:border-[#DC143C] outline-none transition-all text-base"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  tabIndex={-1}
                  data-testid="toggle-password"
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] focus:ring-4 focus:ring-red-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base active:scale-[0.98]"
              data-testid="login-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Entrando...
                </span>
              ) : 'Entrar'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <Link to="/esqueci-senha" className="text-[#DC143C] hover:underline text-sm block">
              Esqueceu sua senha?
            </Link>
            <p className="text-gray-600 text-sm md:text-base">
              Não tem conta?{' '}
              <Link to={`/register${userType ? `?type=${userType}` : ''}`} className="text-[#DC143C] font-semibold hover:underline">
                Cadastre-se
              </Link>
            </p>
          </div>
          
          {/* Trust Elements */}
          <div className="mt-6 flex items-center justify-center gap-4 text-gray-500 text-xs">
            <div className="flex items-center gap-1">
              <FiShield size={14} />
              <span>Dados protegidos</span>
            </div>
            <div className="flex items-center gap-1">
              <FiLock size={14} />
              <span>Conexão segura</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/50 text-xs md:text-sm mt-6">
          © 2025 BarueriFlash. Todos os direitos reservados.
        </p>
        <div className="text-center text-white/40 text-xs mt-2 space-x-4">
          <Link to="/termos" className="hover:text-white/60">Termos de Uso</Link>
          <Link to="/privacidade" className="hover:text-white/60">Privacidade</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
